"use client";

import Image from "next/image";

import cn from "classnames";

import { Link } from "@/components/link/link.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import type { ImageContentCard as SanityImageContentCard } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { Button } from "../button/button.component";

export const ImageContentCard = ({ block }: ImageContentCardProps) => {
  if (!block?.mainImage || !block?.body?.length) return null;

  const mainImageUrl = urlFor(block.mainImage).width(1920).height(1080).url();

  const mobileImageUrl = block.mainImage.mainImageMobile
    ? urlFor(block.mainImage.mainImageMobile).width(800).height(1000).url()
    : null;

  const titlePosition = block.titlePosition ?? "content";
  const titleAtTop = titlePosition === "top";

  return (
    <section
      data-testid="image-content-card"
      className={cn(
        "flex w-full flex-col gap-10 overflow-x-hidden px-8 lg:py-10",
        {
          "bg-primary text-greyscale-body": block.theme === "primary",
          "bg-secondary text-white": block.theme === "secondary",
          "bg-secondary-100 text-white": block.theme === "secondary100",
          "bg-primary-100 text-secondary": block.theme === "tertiary",
          "bg-greyscale-background text-greyscale-body":
            block.theme === "light",
          "text-greyscale-body bg-white": block.theme === "white",
          "py-8": titleAtTop,
        },
      )}>
      {titleAtTop ? (
        <div className="container mx-auto flex flex-col items-center gap-4 text-center">
          {block.subtitle ? (
            <Text
              variant="preHeaderLg"
              component="p"
              className={cn("tracking-wide uppercase", {
                "text-greyscale-headline":
                  block.theme === "light" || block.theme === "white",
                "text-white/90":
                  block.theme === "primary" ||
                  block.theme === "secondary" ||
                  block.theme === "secondary100",
              })}>
              {block.subtitle}
            </Text>
          ) : null}
          <Text
            variant="h3"
            component="h2"
            data-testid="image-content-card-title-top"
            className={cn({
              "text-white":
                block.theme === "secondary" || block.theme === "secondary100",
              "text-greyscale-headline":
                block.theme === "primary" ||
                block.theme === "light" ||
                block.theme === "white" ||
                block.theme === "tertiary",
            })}>
            {block.title}
          </Text>
          {(block?.description || []).length > 0 ? (
            <div
              className={cn("mx-auto w-full max-w-4xl", {
                "text-white":
                  block.theme === "secondary" || block.theme === "secondary100",
                "text-greyscale-body":
                  block.theme === "primary" ||
                  block.theme === "light" ||
                  block.theme === "white",
              })}>
              <RichText>{block.description}</RichText>
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          "container mx-auto grid w-full grid-cols-1 items-start gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-12",
        )}>
        {mainImageUrl && (
          <div
            className={cn(
              "relative order-1 w-full overflow-hidden rounded-none max-lg:ml-[calc(50%-50vw)] max-lg:w-screen max-lg:max-w-[100vw] lg:rounded-2xl",
              block.imagePosition === "left" ? "lg:order-1" : "lg:order-2",
            )}>
            {mobileImageUrl ? (
              <Image
                src={mobileImageUrl}
                alt={block.mainImage?.alt || block.title || ""}
                width={800}
                height={1000}
                className="h-auto w-full object-cover md:hidden"
                sizes="100vw"
              />
            ) : null}
            <Image
              src={mainImageUrl}
              alt={block.mainImage?.alt || block.title || ""}
              width={1920}
              height={1080}
              className={cn(
                "h-auto w-full object-cover",
                mobileImageUrl && "hidden md:block",
              )}
              sizes="(max-width: 1023px) 100vw, 48vw"
            />
            {(block.mainImage?.overlayText || []).length > 0 ? (
              <div
                className="absolute bottom-0 left-0 z-10 max-w-[min(100%,22rem)] p-4 text-left text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)] md:p-6 [&_*]:text-white [&_a]:underline [&_a:hover]:text-white/90 [&_p]:mb-1 [&_p:last-child]:mb-0"
                data-testid="image-content-card-overlay-text">
                <RichText>{block.mainImage.overlayText}</RichText>
              </div>
            ) : null}
          </div>
        )}

        <div
          className={cn(
            "order-2 mb-10 flex min-h-0 w-full flex-col items-start justify-center gap-8 pt-6 md:gap-5 lg:h-full lg:min-h-0 lg:pt-0",
            block.imagePosition === "left" ? "lg:order-2" : "lg:order-1",
          )}>
          {!titleAtTop ? (
            <>
              {block.subtitle ? (
                <Text
                  variant="preHeaderSm"
                  component="p"
                  className={cn("tracking-wide uppercase", {
                    "text-greyscale-headline":
                      block.theme === "light" || block.theme === "white",
                    "text-white":
                      block.theme === "secondary" ||
                      block.theme === "secondary100",
                    "text-white/90": block.theme === "primary",
                  })}>
                  {block.subtitle}
                </Text>
              ) : null}
              <Text
                variant="h3"
                data-testid="image-content-card-title-content"
                className={cn({
                  "text-white":
                    block.theme === "secondary" ||
                    block.theme === "secondary100",
                  "text-greyscale-headline":
                    block.theme === "primary" ||
                    block.theme === "light" ||
                    block.theme === "white" ||
                    block.theme === "tertiary",
                })}>
                {block.title}
              </Text>
              {(block?.description || []).length > 0 ? (
                <div
                  className={cn("w-full", {
                    "text-white [&_[data-testid='text']]:!text-white [&_a]:!text-white [&_a]:underline [&_a:hover]:!text-white/90":
                      block.theme === "secondary" ||
                      block.theme === "secondary100",
                    "text-greyscale-body":
                      block.theme === "primary" ||
                      block.theme === "light" ||
                      block.theme === "white",
                  })}>
                  <RichText>{block.description}</RichText>
                </div>
              ) : null}
            </>
          ) : null}
          <div
            className={cn("w-full", {
              "text-white [&_[data-testid='text']]:!text-white [&_a]:!text-white [&_a]:underline [&_a:hover]:!text-white/90":
                block.theme === "secondary" || block.theme === "secondary100",
              "text-greyscale-body":
                block.theme === "primary" ||
                block.theme === "light" ||
                block.theme === "white",
            })}>
            <RichText>{block.body}</RichText>
          </div>
          {block.ctaLabel ? (
            <Button
              primary={
                block.theme === "secondary" || block.theme === "secondary100"
              }
              secondary={
                block.theme === "primary" ||
                block.theme === "light" ||
                block.theme === "white" ||
                block.theme === "tertiary"
              }
              className={cn("min-w-28 px-8 py-3 uppercase", {})}>
              <Link
                href={block.ctaLink?.url ?? ""}
                target={block.ctaLink?.openInNewTab ? "_blank" : undefined}
                rel={
                  block.ctaLink?.openInNewTab
                    ? "noopener noreferrer"
                    : undefined
                }>
                {block.ctaLabel}
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
};

type ImageContentCardProps = {
  block: { _key: string } & SanityImageContentCard;
};
